import React, { useEffect, useMemo, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listExpenses, deleteExpense } from "../services/expense";
import { getExpensesReportPdf } from "../services/reports";
import { MONTHS, formatCurrencyMZN } from "../utils/paymentHelpers";
import { useConfirm } from "../hook/useConfirm";

const PAGE_SIZE = 8;
const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);

// Thousand-separated "1,234.00 MZN" (formatCurrencyMZN from paymentHelpers,
// same formatting used across the payment screens) instead of a bare
// toFixed(2) - matters once expense values go past 3 digits.
const formatCurrency = formatCurrencyMZN;

const pad2 = (n) => String(n).padStart(2, "0");
const toISODate = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

// First/last day of the given Ano/Mês, as "YYYY-MM-DD" — used to pre-fill
// Data de Início/Data de Fim whenever Ano or Mês change.
const monthDateRange = (year, month) => {
  const firstDay = new Date(Number(year), Number(month) - 1, 1);
  const lastDay = new Date(Number(year), Number(month), 0);
  return { startDate: toISODate(firstDay), endDate: toISODate(lastDay) };
};

const STATUS_STYLES = {
  APPROVED: "bg-success-focus text-success-main",
  PENDING: "bg-warning-focus text-warning-main",
  REJECTED: "bg-danger-focus text-danger-main",
};

const KIND_LABEL_KEY = { FIXA: "despesa.kind.fixed", VARIAVEL: "despesa.kind.variable" };

// Split out of the former DespesasLayer. Same "hide cadastro from the menu"
// philosophy applied to ListaProdutosLayer/ListaTaxasLayer/ListaExtrasLayer:
// cadastro is only reachable via the "Adicionar Despesa" button below (see
// CadastrarDespesaLayer / App.js route `/despesas/lista/cadastrar`).
//
// Filters and the fetch itself follow
// sge_infantil_web/src/pages/expenses/expenses/list/index.js: the list
// endpoint `GET api/expenses/:sucursalId` requires year+month, so those
// (not a free-text search) drive the query. Header (title/subtitle/add
// button) and the stat-card "Resumo" row above the filters follow
// ListaEstudantesLayer. "Imprimir Relatório" scopes to whichever filter is
// actually active against `api/report/expenses/:sucursalId`, the same
// pattern as RelatorioDividasLayer (navigate to /documentos/pdf).
//
// Data de Início/Data de Fim start out mirroring Ano/Mês (via
// monthDateRange), recomputed whenever either changes — but the two scopes
// are mutually exclusive, same as the list endpoint (routes/expenses.js)
// and the report endpoint (routes/report.js) both enforce server-side:
// picking a Data de Fim is what commits to a genuinely custom range
// (isCustomRange), which clears and disables Ano/Mês (they no longer mean
// anything once a range spans other months) and switches BOTH the list
// fetch and "Imprimir Relatório" to the range. Data de Início alone doesn't
// flip the mode — only completing the range (Data de Fim) does. Ano/Mês
// only come back via "Limpar Filtros" (clearFilters), which is also the
// only way back out of a custom range.
//
// Expand/collapse row for `items` (Descrição/Preço, only present on
// Variável expenses created via CadastrarDespesaLayer's itens table) reuses
// the exact pattern from ListaConsumoLayer.jsx.
const ListaDespesasLayer = () => {
  const { t } = useTranslation();
  const { confirm, ConfirmModal } = useConfirm();
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(String(currentYear));
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [startDate, setStartDate] = useState(
    () => monthDateRange(currentYear, new Date().getMonth() + 1).startDate
  );
  const [endDate, setEndDate] = useState(
    () => monthDateRange(currentYear, new Date().getMonth() + 1).endDate
  );
  // True once the user edits Data de Início/Fim away from the Ano/Mês-derived
  // range — a genuinely custom range, mutually exclusive with Ano/Mês (see
  // sge_infantil_core's routes/expenses.js).
  const [isCustomRange, setIsCustomRange] = useState(false);
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState(null);
  const [printing, setPrinting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const handleYearChange = (value) => {
    setYear(value);
    setIsCustomRange(false);
    const range = monthDateRange(value, month);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  const handleMonthChange = (value) => {
    setMonth(value);
    setIsCustomRange(false);
    const range = monthDateRange(year, value);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  // Data de Início alone doesn't switch modes — it only completes once Data
  // de Fim is also picked, which is the actual signal that the operator
  // wants a custom range instead of Ano/Mês.
  const handleStartDateChange = (value) => {
    setStartDate(value);
  };

  const handleEndDateChange = (value) => {
    setEndDate(value);
    setIsCustomRange(true);
    // Ano/Mês no longer apply once a custom range is active — cleared (not
    // just visually disabled) so it's unambiguous which scope is in effect,
    // for both the list and "Imprimir Relatório". Re-populated only by
    // clearFilters.
    setYear("");
    setMonth("");
  };

  const fetchExpenses = (y, m, range) => {
    setLoading(true);
    listExpenses(range ? { startDate: range.startDate, endDate: range.endDate } : { year: y, month: m })
      .then((resp) => setExpenses(resp.data || []))
      .catch(() => toast.error(t("despesa.error.load")))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchExpenses(year, month, isCustomRange ? { startDate, endDate } : null);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, startDate, endDate, isCustomRange]);

  const filtered = expenses;

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goToPage = (next) => {
    if (next < 1 || next > totalPages) return;
    setPage(next);
  };

  const clearFilters = () => {
    const defaultYear = String(currentYear);
    const defaultMonth = String(new Date().getMonth() + 1);
    const range = monthDateRange(defaultYear, defaultMonth);
    setYear(defaultYear);
    setMonth(defaultMonth);
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setIsCustomRange(false);
    setPage(1);
  };

  const totalValue = useMemo(
    () => filtered.reduce((sum, e) => sum + (Number(e.expenseValue) || 0), 0),
    [filtered],
  );
  const pendingCount = filtered.filter((e) => e.status === "PENDING").length;

  const statusLabel = (status) => {
    if (status === "APPROVED") return t("despesa.status.approved");
    if (status === "PENDING") return t("despesa.status.pending");
    if (status === "REJECTED") return t("despesa.status.rejected");
    return status || "-";
  };

  const handleDelete = async (expense) => {
    if (!(await confirm(t("despesa.list.confirm.remove")))) return;
    setDeletingId(expense.id);
    try {
      await deleteExpense(expense.id);
      toast.success(t("despesa.list.remove.success"));
      fetchExpenses(year, month, isCustomRange ? { startDate, endDate } : null);
    } catch (err) {
      toast.error(t("despesa.list.remove.error"));
    } finally {
      setDeletingId(null);
    }
  };

  const handlePrint = async () => {
    setPrinting(true);
    try {
      // Reflect whichever scope is actually active — a custom Data de
      // Início/Fim range, or Ano/Mês — instead of always requesting a
      // single month's report regardless of what's selected.
      const resp = isCustomRange
        ? await getExpensesReportPdf({ startDate, endDate })
        : await getExpensesReportPdf({ year, month });
      const title = isCustomRange
        ? t("despesa.list.print.title.range", { startDate, endDate })
        : t("despesa.list.print.title", { month: MONTHS.find((m) => m.code === month)?.desc, year });
      const filename = isCustomRange
        ? `relatorio-despesas-${startDate}-a-${endDate}.pdf`
        : `relatorio-despesas-${year}-${month}.pdf`;
      navigate("/documentos/pdf", {
        state: {
          url: resp.data.pdf,
          title,
          filename,
        },
      });
    } catch (err) {
      toast.error(t("despesa.list.print.error"));
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='d-flex flex-wrap align-items-center gap-3'>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='solar:wallet-money-bold' />
              </span>
              <div>
                <h6 className='mb-0'>{filtered.length}</h6>
                <span className='text-secondary-light text-sm'>{t("despesa.stat.count")}</span>
              </div>
            </div>
          </div>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-info-focus text-info-main d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='solar:banknote-2-bold' />
              </span>
              <div>
                <h6 className='mb-0'>{formatCurrency(totalValue)}</h6>
                <span className='text-secondary-light text-sm'>{t("despesa.stat.totalValue")}</span>
              </div>
            </div>
          </div>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-warning-focus text-warning-main d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='solar:clock-circle-bold' />
              </span>
              <div>
                <h6 className='mb-0'>{pendingCount}</h6>
                <span className='text-secondary-light text-sm'>{t("despesa.stat.pending")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24'>
              <div>
                <h6 className='mb-4'>{t("despesa.section.list")}</h6>
                <p className='text-secondary-light mb-0 text-sm'>{t("despesa.list.subtitle")}</p>
              </div>
              <div className='d-flex flex-wrap align-items-center gap-2'>
                <button
                  type='button'
                  className='btn border border-primary-600 text-primary-600 d-flex align-items-center gap-2'
                  onClick={handlePrint}
                  disabled={printing}
                >
                  <Icon icon='solar:printer-linear' />
                  {printing ? t("global.loading") : t("despesa.list.print")}
                </button>
                <button
                  type='button'
                  className='btn btn-primary-600 d-flex align-items-center gap-2'
                  onClick={() => navigate("/despesas/lista/cadastrar")}
                >
                  <Icon icon='ic:baseline-plus' />
                  {t("despesa.list.add")}
                </button>
              </div>
            </div>

            <div className='p-16 radius-8 bg-neutral-50 mb-24'>
              <div className='row gy-3 align-items-end'>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("despesa.field.year")}</label>
                  <select
                    className='form-control form-select'
                    value={year}
                    disabled={isCustomRange}
                    onChange={(e) => handleYearChange(e.target.value)}
                  >
                    {isCustomRange && <option value=''>{t("estudante.select.placeholder")}</option>}
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("despesa.field.month")}</label>
                  <select
                    className='form-control form-select'
                    value={month}
                    disabled={isCustomRange}
                    onChange={(e) => handleMonthChange(e.target.value)}
                  >
                    {isCustomRange && <option value=''>{t("estudante.select.placeholder")}</option>}
                    {MONTHS.map((m) => (
                      <option key={m.code} value={m.code}>
                        {m.desc}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("despesa.field.startDate")}</label>
                  <input
                    type='date'
                    className='form-control'
                    value={startDate}
                    max={endDate || undefined}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                  />
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <label className='form-label text-sm mb-8'>{t("despesa.field.endDate")}</label>
                  <input
                    type='date'
                    className='form-control'
                    value={endDate}
                    min={startDate || undefined}
                    onChange={(e) => handleEndDateChange(e.target.value)}
                  />
                </div>
                <div className='col-lg-2'>
                  <button
                    type='button'
                    className='btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center gap-2'
                    onClick={clearFilters}
                  >
                    <Icon icon='ic:round-refresh' />
                    {t("despesa.list.filter.clear")}
                  </button>
                </div>
              </div>
            </div>

            <div className='table-responsive scroll-sm'>
              <table className='table bordered-table mb-0'>
                <thead>
                  <tr>
                    <th scope='col' style={{ width: 40 }} />
                    <th scope='col'>{t("despesa.list.col.kind")}</th>
                    <th scope='col'>{t("despesa.list.col.category")}</th>
                    <th scope='col'>{t("despesa.list.col.fund")}</th>
                    <th scope='col'>{t("despesa.list.col.name")}</th>
                    <th scope='col'>{t("despesa.field.date")}</th>
                    <th scope='col'>{t("despesa.field.value")}</th>
                    <th scope='col'>{t("global.status")}</th>
                    <th scope='col' className='text-end'>
                      {t("global.col.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={9} className='text-center text-secondary-light py-32'>
                        {t("global.loading")}
                      </td>
                    </tr>
                  )}
                  {!loading && pageItems.length === 0 && (
                    <tr>
                      <td colSpan={9} className='text-center text-secondary-light py-32'>
                        {t("despesa.hint.noExpenses")}
                      </td>
                    </tr>
                  )}
                  {pageItems.map((e) => {
                    const hasItems = (e.items || []).length > 0;
                    const isExpanded = expandedId === e.id;
                    return (
                      <React.Fragment key={e.id}>
                        <tr
                          role={hasItems ? "button" : undefined}
                          onClick={hasItems ? () => setExpandedId(isExpanded ? null : e.id) : undefined}
                          className={isExpanded ? "bg-neutral-50" : ""}
                        >
                          <td>
                            {hasItems && (
                              <Icon
                                icon={isExpanded ? "ic:round-expand-less" : "ic:round-expand-more"}
                                className='text-secondary-light text-xl'
                              />
                            )}
                          </td>
                          <td>{e.expenseKind ? t(KIND_LABEL_KEY[e.expenseKind] || e.expenseKind) : "-"}</td>
                          <td>{e.expenseType || "-"}</td>
                          <td>{e.expenseFund || "-"}</td>
                          <td className='fw-semibold text-primary-light'>{e.expenseName || e.expenseType || "-"}</td>
                          <td>{typeof e.expenseDate === "string" ? e.expenseDate : "-"}</td>
                          <td>{formatCurrency(e.expenseValue)}</td>
                          <td>
                            <span
                              className={`px-10 py-4 radius-8 fw-medium text-sm ${
                                STATUS_STYLES[e.status] || "bg-neutral-200 text-neutral-600"
                              }`}
                            >
                              {statusLabel(e.status)}
                            </span>
                          </td>
                          <td className='text-end'>
                            <button
                              type='button'
                              className='btn btn-outline-danger-600 btn-sm d-inline-flex align-items-center gap-1'
                              disabled={deletingId === e.id}
                              onClick={(evt) => {
                                evt.stopPropagation();
                                handleDelete(e);
                              }}
                            >
                              <Icon icon='mingcute:delete-2-line' /> {t("despesa.list.action.remove")}
                            </button>
                          </td>
                        </tr>
                        {hasItems && isExpanded && (
                          <tr>
                            <td colSpan={9} className='bg-neutral-25 border-top p-0'>
                              <table className='table table-sm mb-0'>
                                <thead>
                                  <tr>
                                    <th>{t("despesa.field.items.description")}</th>
                                    <th>{t("despesa.field.items.price")}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {e.items.map((item, idx) => (
                                    <tr key={idx}>
                                      <td>{item.description}</td>
                                      <td>{formatCurrency(item.price)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mt-16'>
              <span className='text-secondary-light text-sm'>
                {filtered.length} {t("despesa.list.total")}
              </span>
              {!loading && filtered.length > PAGE_SIZE && (
                <ul className='pagination d-flex flex-wrap align-items-center gap-2 mb-0'>
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                    >
                      <Icon icon='ep:d-arrow-left' className='text-xl' />
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <li key={p} className='page-item'>
                      <button
                        type='button'
                        className={`page-link fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px ${
                          p === page ? "bg-primary-600 text-white" : "bg-primary-50 text-secondary-light"
                        }`}
                        onClick={() => goToPage(p)}
                      >
                        {p}
                      </button>
                    </li>
                  ))}
                  <li className='page-item'>
                    <button
                      type='button'
                      className='page-link text-secondary-light fw-medium radius-4 border-0 px-10 py-10 d-flex align-items-center justify-content-center h-32-px w-32-px bg-base'
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                    >
                      <Icon icon='ep:d-arrow-right' className='text-xl' />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
      {ConfirmModal}
    </div>
  );
};

export default ListaDespesasLayer;
